import { absurd } from "../../../utils/absurd";
import { knownStatusOptions, knownStatusRules, statusTransitions, type TKnownStatusRules } from "./consts";
import type { TProduct } from "../../../data";
import type { TStatusOption } from "./consts";
import type { TGridSettings } from "../../../data";

import { objectEntries } from "../../../utils";

export function checkCanBroadcast(
  permissions: TProduct["packageByPackage"]["permissions"] | null | undefined,
): boolean {
  return permissions?.some((perm) => perm.permission === "broadcast" || perm.permission === "write") ?? false;
}

export function verifyKnownStatusRules(
  rules: TKnownStatusRules | undefined,
  resolvedRules: Required<TKnownStatusRules>,
) {
  return !rules || objectEntries(rules).every(([rule, bool]) => bool === resolvedRules[rule]);
}

export function genSelectStatusOptions({
  resolvedRules,
  globalOptions,
  settings,
}: {
  resolvedRules: Required<TKnownStatusRules>;
  globalOptions: Array<TStatusOption>;
  settings: Pick<TGridSettings, "hide_eod" | "hide_private" | "hide_broadcast" | "hide_global"> | null | undefined;
}) {
  return [
    ...knownStatusOptions
      // to compare the hard coded rules in statusOptions with the resolved rules
      // belonging to the product in question.
      .filter(({ value }) => {
        let userDisabled = false;
        switch (value) {
          case "eod":
            userDisabled = !!settings?.hide_eod;
            break;
          case "private":
            userDisabled = !!settings?.hide_private;
            break;
          case "broadcast":
            userDisabled = !!settings?.hide_broadcast;
            break;
          case "hybrid_broadcast":
            userDisabled = false;
            break;
          case "listen":
            userDisabled = false;
            break;
          default:
            absurd(value);
        }

        return !userDisabled && verifyKnownStatusRules(knownStatusRules[value], resolvedRules);
      }),
    ...(settings?.hide_global ? [] : globalOptions),
  ] satisfies TStatusOption[];
}

export const isGlobalStatus = (status: string | undefined) => {
  if (!status) return false;
  return status.length === 36;
};

export function genStatusTransitionActions({ from, to }: { from: string | undefined; to: string }) {
  const isFromGlobal = isGlobalStatus(from);

  return statusTransitions.find((entry) => {
    if (entry.to !== to) return false;

    if (entry.from === from) return true;

    if (entry.from === "global" && isFromGlobal) return true;

    return entry.from === "*";
  });
}
